/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormHelperText,
  MenuItem,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  useForm,
  getFieldProps,
  getFormProps,
  getSubmitButtonProps,
  PlainTextEditor,
} from '@marageti/z4-lib';
import { zodResolver } from '@hookform/resolvers/zod';
import { Review } from '@marageti/z4-sdk/lib/travel';
import { Controller, FieldValues, SubmitHandler } from 'react-hook-form';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import LoadingButton from '@mui/lab/LoadingButton';
import { getRatingNumber } from '../../../utils/helpers';
import ReviewEditSchema from './edit-review-form-util';

type EditUserViewProps = {
  review: Review | undefined;
  submissionError?: ApiError;
  onCancel: () => void;
  onSubmit: SubmitHandler<FieldValues>;
};

const EditReviewForm = ({
  review,
  submissionError = undefined,
  onCancel,
  onSubmit,
}: EditUserViewProps) => {
  const {
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    register,
    setValue,
    control,
  } = useForm({
    defaultValues: {
      ...review,
      rating: Math.max(1, getRatingNumber(review?.rating)),
      published: review?.published ? 'Published' : 'Unpublished',
      title: review?.title ? review.title : undefined,
      mainReview: review?.mainReview ? review.mainReview : undefined,
      bestParts: review?.bestParts ? review.bestParts : undefined,
      doDifferently: review?.doDifferently ? review.doDifferently : undefined,
      agentReply: review?.agentReply.reply ? review.agentReply.reply : undefined,
    },
    resolver: zodResolver(ReviewEditSchema),
  });

  return (
    <form name="trip-match-form" {...getFormProps(handleSubmit(onSubmit))}>
      <Stack spacing={5}>
        <Box>
          <TextField
            label="Status"
            select
            defaultValue={review?.published ? 'Published' : 'Unpublished'}
            {...register('published', { required: true })}
            error={!!errors.published}
            onChange={(e) => setValue('published', e.target.value)}
            sx={{ width: { desktop: '304px', mobile: '100%' } }}
          >
            <MenuItem value="Published">Published</MenuItem>
            <MenuItem value="Unpublished">Unpublished</MenuItem>
          </TextField>
          <FormHelperText sx={{
            maxWidth: 'none',
            marginLeft: 0,
          }}
          >
            Published reviews are visible to other travelers and partners within the Zicasso platform.
          </FormHelperText>
        </Box>
        <Box>
          <Controller
            control={control}
            name="rating"
            render={({ field, fieldState }) => (
              <Stack spacing={0.5}>
                <Typography component="legend" variant="bodyBold">
                  Rating
                </Typography>
                <Rating
                  name={field.name}
                  value={field.value}
                  onChange={(_, value) => {
                    field.onChange(value ?? field.value ?? 1);
                  }}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  precision={1}
                />
                {fieldState.error?.message ? (
                  <FormHelperText error>{fieldState.error.message}</FormHelperText>
                ) : null}
              </Stack>
            )}
          />
        </Box>
        <TextField
          label="Title"
          fullWidth
          defaultValue={review?.title}
          {...register('title', { required: true })}
          {...getFieldProps('title', errors)}
        />
        <Box>
          <Typography variant="bodyBold">
            What are your overall comments on the trip and the travel company?
          </Typography>
          <Controller
            control={control}
            name="mainReview"
            rules={{ required: true }}
            render={({ field }) => (
              <PlainTextEditor
                defaultValue={review?.mainReview}
                editorName="mainReview-editor"
                error={!!errors.mainReview}
                helperText={errors.mainReview?.message}
                label="Comments"
                onChange={field.onChange}
                useLists
                sx={{
                  minHeight: { desktop: '240px' },
                  maxHeight: '240px',
                }}
              />
            )}
          />
        </Box>
        <Box>
          <Typography variant="bodyBold">
            What were the most enjoyable or memorable parts of your trip?
          </Typography>
          <Controller
            control={control}
            name="bestParts"
            rules={{ required: true }}
            render={({ field }) => (
              <PlainTextEditor
                defaultValue={review?.bestParts}
                editorName="bestParts-editor"
                error={!!errors.bestParts}
                helperText={errors.bestParts?.message}
                label="Comments"
                onChange={field.onChange}
                useLists
                sx={{
                  minHeight: { desktop: '240px' },
                  maxHeight: '240px',
                }}
              />
            )}
          />
        </Box>
        <Box>
          <Typography variant="bodyBold">
            If you could take your trip again, what would you do differently?
          </Typography>
          <Controller
            control={control}
            name="doDifferently"
            rules={{ required: true }}
            render={({ field }) => (
              <PlainTextEditor
                defaultValue={review?.doDifferently}
                editorName="doDifferently-editor"
                error={!!errors.doDifferently}
                helperText={errors.doDifferently?.message}
                label="Comments"
                onChange={field.onChange}
                useLists
                sx={{
                  minHeight: { desktop: '240px' },
                  maxHeight: '240px',
                }}
              />
            )}
          />
        </Box>
        <Box>
          <Typography variant="bodyBold">
            Reply from Specialist:
          </Typography>
          <Controller
            control={control}
            name="agentReply"
            rules={{ required: true }}
            render={({ field }) => (
              <PlainTextEditor
                defaultValue={review?.agentReply.reply}
                editorName="agentReply-editor"
                error={!!errors.agentReply}
                helperText={errors.agentReply?.message}
                label="Comments"
                onChange={field.onChange}
                useLists
                sx={{
                  minHeight: { desktop: '240px' },
                  maxHeight: '240px',
                }}
              />
            )}
          />
        </Box>
        {submissionError && (
        <Alert severity="error">
          <AlertTitle>There was an error on our end.</AlertTitle>
          {`Code: ${submissionError.code}: ${submissionError.message}`}
          <br />
          {submissionError.id && `ID: ${submissionError.id}`}
        </Alert>
        )}
        <Stack direction="row" spacing={2} mt="24px">
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Save
          </LoadingButton>
          <Button variant="outlined" onClick={onCancel}>Cancel</Button>
        </Stack>
      </Stack>
    </form>
  );
};

export default EditReviewForm;
